<template>
  <div class="h-full w-full max-w-full overflow-hidden rounded-[10px] border border-[#e7e7e7] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
    <FlexCol class="relative h-full w-full max-w-full table-outer bg-white text-[#13161b]">
      <div class="grow overflow-auto">
        <table class="w-full grow">
          <thead class="header top-0 z-[2] h-10 uppercase">
            <tr v-if="headers" class="border-b border-[#e7e7e7] bg-[#f9fafb]">
              <th
                v-for="(header, i) in headers"
                :key="i"
                class="select-none text-left text-[13px] font-semibold tracking-[-0.154px] text-[#13161b]"
                :class="[cellSpacing, i === headers.length - 1 && stickyActions ? 'sticky right-0 z-[3] bg-[#f9fafb]' : '']">
                <Flex gap-3 items-center :class="[header.headerClass]">
                  <slot :name="`td-${header.field}`" :item="header">
                    {{ header.title }}

                    <div v-if="!header.disableOrder">
                      <i class="fa-solid fa-sort-down ml-2 cursor-pointer text-[12px]" @click="setOrder(header, 2, header.table)" v-if="getStatus(header) == 1" />
                      <i class="fa-solid fa-sort-up ml-2 cursor-pointer text-[12px]" @click="setOrder(header, 0, header.table)" v-else-if="getStatus(header) == 2" />
                      <i class="fa-solid fa-sort ml-2 cursor-pointer text-[12px]" @click="setOrder(header, 1, header.table)" v-else />
                    </div>
                  </slot>
                </Flex>
              </th>
              <th v-if="expanded" :class="[cellSpacing]"></th>
            </tr>
          </thead>

          <tbody>
            <template v-for="(item, index) in processedItems" :key="index">
              <tr
                @click="sendLeftClick(item)"
                @click.right.prevent="sendRightClick(item)"
                @dblclick="sendDoubleClick(item)"
                class="group cursor-pointer transition-colors"
                :class="[
                  computedClass(item),
                  { 'border-b border-[#e7e7e7]': !expanded || !item.expanded },
                  { '!bg-sky-50': compareItem(item) },
                  { 'hover:bg-slate-50': !compareItem(item) },
                  { 'text-red-400': item.errorLine },
                  { 'divide-x': cellBorder },
                ]">
                <td
                  v-for="(header, hIndex) in headers"
                  :key="header"
                  :class="[
                    header.itemClass,
                    'text-nowrap align-middle text-[14px] tracking-[-0.154px] text-[#13161b]',
                    cellSpacing,
                    hIndex === headers.length - 1 && stickyActions ? 'sticky right-0 bg-white' : '',
                  ]">
                  <div v-if="item" class="h-full" :class="[header.field === 'actions' ? 'flex justify-center' : '']">
                    <slot :name="header.field" :item="item" :rowIndex="index">
                      {{ getItemValue(item, header) }}
                    </slot>
                  </div>
                </td>

                <td v-if="expanded && mapNotExpandedItems(item)" class="w-4">
                  <IconSolid :icon="item.expanded ? 'fa-chevron-down' : 'fa-chevron-up'" @click="item.expanded = !item.expanded" />
                </td>
              </tr>

              <TransitionX>
                <tr v-if="expanded || item.expanded" class="border-b border-[#e7e7e7] bg-slate-50">
                  <td :colspan="headers?.length + 1">
                    <slot name="expanded" :item="item"></slot>
                  </td>
                </tr>
              </TransitionX>
            </template>
          </tbody>

          <tfoot>
            <slot name="footer" />
          </tfoot>
        </table>
      </div>
      <div class="relative h-[2px] w-full overflow-hidden bg-primary-200" v-if="loading">
        <div class="absolute inset-y-0 left-0 w-1/3 bg-primary-500 animate-[loading_1s_linear_infinite]"></div>
      </div>
      <div class="table-footer border-t border-[#e7e7e7] bg-[#f9f9f9] px-3 py-2">
        <p class="py-5 text-center text-[14px] italic tracking-[-0.154px] text-slate-500" v-if="items && items?.length < 1">Nenhum item a ser exibido.</p>
        <div v-if="!disablePagination">
          <NavigationBar
            v-if="items?.length"
            v-model="localControl.page"
            v-model:limit="localControl.limit"
            :disablePagination="disablePagination"
            :totalItems="localControl.total"
            :totalPages="localControl.pages">
            <template #left>
              <Flex v-if="!disableOrder" gap-2 center class="border-l border-[#d6d9de] pl-3 text-[12px] text-slate-500">
                Ordenado por:
                <div v-for="(order, i) in localControl.order" :key="i" class="rounded-full border border-[#d6d9de] bg-white px-3 py-1 text-[12px]">
                  {{ order.name }}
                  <i class="fa-solid fa-caret-down ml-2 cursor-pointer" v-if="order.direction == 'DESC'" />
                  <i class="fa-solid fa-caret-up ml-2 cursor-pointer" v-if="order.direction == 'ASC'" />
                  <i class="fa-solid fa-x ml-2 cursor-pointer text-[0.8em]" @click="removeOrder(order.column)" />
                </div>
              </Flex>
            </template>
          </NavigationBar>
        </div>
      </div>
    </FlexCol>
  </div>

  <div class="mt-3 flex justify-end text-xs text-red-400" v-if="error">
    <b>{{ error }}</b>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const emit = defineEmits(['update:modelValue', 'update:loading', 'selectedItem', 'rightClick', 'doubleClick'])

const props = defineProps({
  modelValue: Object,
  headers: {
    type: Array,
    default: [],
  },
  items: {
    type: Array,
    default: [],
  },
  dense: Boolean,
  ultraDense: Boolean,
  expanded: Boolean,
  loading: Boolean,
  disableOrder: Boolean,
  disablePagination: Boolean,
  offline: Boolean,
  stickyActions: {
    type: Boolean,
    default: false,
  },
})

const localControl = ref(props.modelValue)
const selectedItem = ref(null)

const cellSpacing = computed(() => {
  if (props.ultraDense) {
    return 'px-2 py-1 text-[12px]'
  }
  if (props.dense) {
    return 'px-3 py-[10px] text-[14px] leading-5'
  }
  return 'px-4 py-3 text-[14px] leading-5'
})

const processedItems = computed(() => {
  let items = props.items || []

  if (props.offline && items.length > 0) {
    if (localControl.value.order && localControl.value.order.length > 0) {
      items = [...items]

      items.sort((a, b) => {
        for (const orderRule of localControl.value.order) {
          const column = orderRule.column
          let aVal = a[column]
          let bVal = b[column]

          if (aVal == null && bVal == null) continue
          if (aVal == null) return 1
          if (bVal == null) return -1

          if (typeof aVal === 'string' && typeof bVal === 'string') {
            aVal = aVal.toLowerCase()
            bVal = bVal.toLowerCase()
          }

          let comparison = 0
          if (aVal < bVal) comparison = -1
          else if (aVal > bVal) comparison = 1

          if (comparison !== 0) {
            return orderRule.direction === 'DESC' ? -comparison : comparison
          }
        }
        return 0
      })
    }

    if (localControl.value.limit && localControl.value.page) {
      const start = (localControl.value.page - 1) * localControl.value.limit
      const end = start + localControl.value.limit

      localControl.value.total = items.length
      localControl.value.pages = Math.ceil(items.length / localControl.value.limit)

      items = items.slice(start, end)
    }
  }

  if (props.startsExpanded) {
    return items.map((item) => ({
      ...item,
      expanded: true,
    }))
  }

  return items
})

const computedClass = (item) => {
  return item.lineClass
}

const setOrder = (header, value, tableName) => {
  const columnName = header.orderField ?? header.field

  const newOrder = {
    name: header.title,
    table: tableName,
    column: columnName,
    direction: value == 1 ? 'DESC' : 'ASC',
  }

  if (header.raw) {
    newOrder.raw = header.raw
  }

  const index = localControl.value.order.findIndex((el) => {
    if (tableName) {
      return el.column === columnName && el.table === tableName
    }
    return el.column === columnName
  })

  if (index < 0 && value > 0) {
    localControl.value.order.push(newOrder)
  } else if (index >= 0 && value > 0) {
    localControl.value.order[index] = newOrder
  } else if (index >= 0 && value == 0) {
    localControl.value.order.splice(index, 1)
  }
}

const getStatus = (header) => {
  const columnName = header.orderField ?? header.field
  const hasTable = !!header.table

  const index = localControl.value.order.findIndex((el) => {
    if (hasTable) {
      return el.column === columnName && el.table === header.table
    }
    return el.column === columnName
  })

  if (index === -1) return 0

  return localControl.value.order[index].direction === 'DESC' ? 1 : 2
}

const removeOrder = (column) => {
  const index = localControl.value.order.findIndex((el) => el.column == column)
  localControl.value.order.splice(index, 1)
}

const sendRightClick = (item) => {
  emit('rightClick', item)
}

const sendDoubleClick = (item) => {
  emit('doubleClick', item)
}

const sendLeftClick = (item) => {
  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const compareItem = (item) => {
  return JSON.stringify(item) == selectedItem.value
}

const getItemValue = (item, header) => {
  const rawValue = item[header.field]
  if (header.format && typeof header.format == 'function') {
    return header.format(rawValue, item)
  }
  return rawValue
}

const mapNotExpandedItems = (item) => {
  if (!props.notExpandFieldsWith || props.notExpandFieldsWith?.length === 0) {
    return true
  }

  const conditions = props.notExpandFieldsWith.map((field) => {
    return item[field.field] !== field.value
  })

  return conditions.every(Boolean)
}

const getSelectedIndex = () => {
  if (!selectedItem.value) return -1
  const list = processedItems.value || []
  return list.findIndex((it) => JSON.stringify(it) === selectedItem.value)
}

const selectByIndex = (index) => {
  const list = processedItems.value || []
  if (!list.length) return

  const bounded = Math.max(0, Math.min(index, list.length - 1))
  const item = list[bounded]

  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const moveSelection = (delta) => {
  const list = processedItems.value || []
  if (!list.length) return

  const currentIdx = getSelectedIndex()

  if (currentIdx === -1) {
    selectByIndex(delta > 0 ? 0 : list.length - 1)
    return
  }

  selectByIndex(currentIdx + delta)
}

const onKeyDown = (e) => {
  const tag = e.target?.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    moveSelection(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    moveSelection(-1)
  }
}

watch(
  () => localControl.value,
  () => {},
  { deep: true },
)

onMounted(() => {
  window.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown)
})
</script>

<style>
.table-outer {
  overflow-x: auto;
}

thead {
  position: sticky;
  z-index: 3;
  top: 0;
}

.table-footer {
  position: sticky;
  z-index: 2;
  bottom: 0;
}

@keyframes loading {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}
</style>
